import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { fetchApprovedReviews, fetchMyReview, submitReview, type MyReviewStatus } from '@/lib/productReviewsApi';
import type { ProductReview } from '@/types/product';

interface ProductReviewsProps {
  sku: string;
  skinTypes: string[];
  reviews: ProductReview[];
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`text-xs ${s <= rating ? 'ri-star-fill text-[#7B1113]' : 'ri-star-line text-[#E8E8E6]'}`}
        ></i>
      ))}
    </div>
  );
}

function StarPicker({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          className="cursor-pointer"
          aria-label={`${s} star`}
        >
          <i className={`text-xl ${s <= value ? 'ri-star-fill text-[#7B1113]' : 'ri-star-line text-[#E8E8E6]'}`}></i>
        </button>
      ))}
    </div>
  );
}

export default function ProductReviews({ sku, skinTypes, reviews: curatedReviews }: ProductReviewsProps) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [filter, setFilter] = useState<'all' | '5' | '4' | 'photos'>('all');
  const [customerReviews, setCustomerReviews] = useState<ProductReview[]>([]);
  const [myReview, setMyReview] = useState<MyReviewStatus | null>(null);
  const [rating, setRating] = useState(0);
  const [skinType, setSkinType] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    const approved = await fetchApprovedReviews(sku);
    setCustomerReviews(approved);
    if (currentUser) {
      const mine = await fetchMyReview(sku, currentUser.id);
      setMyReview(mine);
    } else {
      setMyReview(null);
    }
  }, [sku, currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  const reviews = [...customerReviews, ...curatedReviews];
  const avgRating = reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;

  const filtered = filter === 'all'
    ? reviews
    : filter === 'photos'
      ? reviews // mock: all have no photo flag, show all for now
      : reviews.filter((r) => r.rating === Number(filter));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || rating === 0 || !text.trim()) return;
    setSubmitting(true);
    try {
      await submitReview({
        sku,
        customerId: currentUser.id,
        customerName: currentUser.name,
        skinType,
        rating,
        reviewText: text.trim(),
      });
      showToast(t('products.reviewSubmitted', 'Thank you! Your review has been submitted for approval.'));
      setRating(0);
      setSkinType('');
      setText('');
      await load();
    } catch {
      showToast(t('products.reviewSubmitError', 'Could not submit your review. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E8E8E6]">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-3xl font-serif font-bold text-[#2A2A2A]">{avgRating.toFixed(1)}</span>
              <span className="text-sm text-[#8A8A8A] font-light">/ 5</span>
            </div>
            <StarRating rating={Math.round(avgRating)} />
          </div>
          <div className="h-10 w-px bg-[#E8E8E6] hidden md:block" />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-[#2A2A2A]">{reviews.length} {t('products.reviews')}</span>
            <span className="text-[10px] text-[#8A8A8A] font-light">
              {reviews.filter((r) => r.verified).length} {t('products.verifiedPurchases')}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          {(['all', '5', '4', 'photos'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                filter === f
                  ? 'bg-[#2A2A2A] text-white'
                  : 'bg-[#F0EFED] text-[#8A8A8A] hover:text-[#2A2A2A]'
              }`}
            >
              {f === 'all' ? t('products.filterAll') : f === 'photos' ? t('products.filterPhotos') : `${f}★`}
            </button>
          ))}
        </div>
      </div>

      {/* Write a review */}
      <div className="mb-6 p-5 rounded-xl bg-[#FAFAF9] border border-[#F0EFED]">
        <h3 className="text-sm font-medium text-[#2A2A2A] mb-3">{t('products.writeReview')}</h3>
        {!currentUser ? (
          <Link to="/login" className="text-xs text-[#7B1113] hover:underline">
            {t('products.loginToReview')}
          </Link>
        ) : myReview ? (
          <p className="text-xs text-[#8A8A8A]">
            {myReview.status === 'pending'
              ? t('products.reviewPending')
              : myReview.status === 'rejected'
                ? t('products.reviewRejected')
                : null}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-[#8A8A8A] uppercase tracking-wider">{t('products.yourRating')}</span>
                <StarPicker value={rating} onChange={setRating} />
              </div>
              {skinTypes.length > 0 && (
                <div className="flex flex-col gap-1 sm:ml-auto">
                  <span className="text-[10px] text-[#8A8A8A] uppercase tracking-wider">{t('products.yourSkinType')}</span>
                  <select
                    value={skinType}
                    onChange={(e) => setSkinType(e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-[#E8E8E6] text-xs text-[#2A2A2A] focus:outline-none focus:border-[#7B1113]"
                  >
                    <option value="">{t('products.selectSkinType')}</option>
                    {skinTypes.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('products.reviewPlaceholder')}
              rows={3}
              required
              className="w-full px-3 py-2 rounded-xl border border-[#E8E8E6] text-sm text-[#2A2A2A] placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#7B1113] resize-none"
            />
            <button
              type="submit"
              disabled={submitting || rating === 0 || !text.trim()}
              className="self-end px-6 py-2.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors disabled:opacity-50 cursor-pointer"
            >
              {t('products.submitReview')}
            </button>
          </form>
        )}
      </div>

      {/* Review list */}
      <div className="flex flex-col gap-4">
        {filtered.map((review, i) => (
          <div key={i} className="p-4 rounded-xl bg-[#FAFAF9] border border-[#F0EFED]">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[#2A2A2A]">{review.name}</span>
                  {review.verified && (
                    <span className="text-[9px] tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F] font-medium">
                      {t('products.verified')}
                    </span>
                  )}
                </div>
                <span className="text-[10px] text-[#8A8A8A] font-light">
                  {[review.skin, review.date].filter(Boolean).join(' · ')}
                </span>
              </div>
              <StarRating rating={review.rating} />
            </div>
            <p className="text-xs text-[#8A8A8A] font-light leading-relaxed">{review.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
